/** @module csi/types */
define([
	"lodash",
	"src/c8-platform/type/format",
	"csi/locale",
	"csi/sessionConstants",
	"csi/utility/dateUtility",
	"csi/utility/numberUtility",
	"csi/xml"
],
	function(
		_,
		{
			FORMAT_ALPHANUMERIC,
			FORMAT_BARCODE,
			FORMAT_COMMENT,
			FORMAT_COMMENT_LATEST,
			FORMAT_DATE,
			FORMAT_DATE_SHORT,
			FORMAT_DATETIME,
			FORMAT_DATETIME_SECONDS,
			FORMAT_DIFF,
			FORMAT_DIFF_YEAR,
			FORMAT_EMAIL,
			FORMAT_IMAGE,
			FORMAT_INTEGER,
			FORMAT_LOCALIZED,
			FORMAT_LOCALIZED_RICH,
			FORMAT_LOCALIZED_TEXTAREA,
			FORMAT_PHONE,
			FORMAT_REFERENCED_IMAGE,
			FORMAT_RICH,
			FORMAT_SAME_DAY,
			FORMAT_SAME_YEAR,
			FORMAT_SELECTION,
			FORMAT_TEXTAREA,
			FORMAT_TIME,
			FORMAT_TIME_SHORT,
			FORMAT_UNIVERSAL_DATE,
			FORMAT_UNIVERSAL_TIME,
			FORMAT_URL,
		},
		locale,
		sessionConstants,
		dateUtility,
		numberUtility,
		xml
	) {

		const {
			DateRangeSelection,
			DateRangeResources,
		} = dateUtility;

		/** @property {number} MAX_3DFILE_SIZE - sets the maximum file size for 3D obj files */
		const MAX_3DFILE_SIZE = 600000000;

		/** @property {string} XSMALL - column width */
		const XSMALL = "XS";

		/** @property {string} SMALL - column width */
		const SMALL = "S";

		/** @property {string} MEDIUM - column width */
		const MEDIUM = "M";

		/** @property {string} LARGE - column width */
		const LARGE = "L";

		/** @property {string} XLARGE - column width */
		const XLARGE = "XL";

		/** @property {number} COLUMN_WIDTH_MAX - maximum width of table column in pixels */
		const COLUMN_WIDTH_MAX = 700;

		/** @property {number} COLUMN_WIDTH_MIN - minimum width of table column in pixels */
		const COLUMN_WIDTH_MIN = 21;

		/** @property {number} COLUMN_WIDTH_CHECKBOX - width of table column in pixels when showing checkbox/mark */
		const COLUMN_WIDTH_CHECKBOX = 37;

		/** @property {RegExp} COLUMN_WIDTH_RE - regular expression of allowed column widths if not a number */
		const COLUMN_WIDTH_RE = /^(XS|S|M|L|XL)$/;

		/** @property {Object<string,number>} COLUMN_WIDTHS - mapping of standard column width descriptors to their pixel size */
		const COLUMN_WIDTHS = {
			[XSMALL]: 63,
			[SMALL]: 125,
			[MEDIUM]: 200,
			[LARGE]: 300,
			[XLARGE]: 600
		};

		/** @property {Object<string,number>} ICON_ENUM_FORMATS - enumlists that are rendered as icons */
		const ICON_ENUM_FORMATS = {
			ActivityIcon: 1,
			KpiColor: 1,
			KpiComplete: 1,
			KpiTrendDown: 1,
			KpiTrendUp: 1
		};

		// miscellaneous RegExp
		var RE_TAGS = /"[^"]*"|\S+/g;

		/**
		 * Javascript rounding is inconsistent, so use technique below. Reference: http://www.jacklmoore.com/notes/rounding-in-javascript/
		 * @param {number} value
		 * @param {number} decimals
		 * @return {number}
		 */
		function _round(value, decimals) {
			return numberUtility.numberRound(value, decimals);
		}

		function _formatTime(/*Number*/num, /*Number*/denom, /*String*/res, /*Boolean*/before) {
			// summary: format time difference given numerator and denominator in milliseconds with resource id for denominator range
			var n = Math.round(num / denom);
			if (n != 1) {
				res += "s";
			}

			return locale.resource("TIME_DIFF" + (before ? "_BEFORE" : "_AFTER"), [n, locale.resource("TIME_" + res)]);
		}

		// initialize after session is initialized
		locale.deferred.then(function() {
			const {
				datePattern,
				decimalSeparator,
				groupSeparator,
				timePattern,
			} = sessionConstants;

			const loc = locale.getLocale(true);

			// register dateutility locale properties
			dateUtility.registerDateLocaleProperties(loc, datePattern, timePattern);

			// register numeraljs locale properties
			numberUtility.registerNumberLocaleProperties(decimalSeparator, groupSeparator, loc);
		});

		/**
		 * Return string representing given date relative to todays date if within a week either way, otherwise
		 * returns absolute date string without year if day in same year (with year otherwise).
		 * If within a week, then string is formatted as "N days ago" or "in N days". If within one day returns
		 * Yesterday or Tomorrow, or Today if same day.
		 * @param {Date} dt
		 * @param {boolean} [showTimeIfToday]
		 * @return {string}
		 */
		function getFormattedDateDifference(dt, showTimeIfToday) {
			const diff = this.getDateDifference(dt);
			const n = Math.abs(diff);
			const week = 7;

			if (!diff) {
				return showTimeIfToday ? dateUtility.formatTime(dt, FORMAT_TIME) : locale.resource("Today");
			} else if (diff === -1) {
				return locale.resource("Yesterday");
			} else if (diff === 1) {
				return locale.resource("Tomorrow");
			} else if (Math.abs(diff) <= week) {
				return locale.resource("TIME_DIFF" + ((diff < 0) ? "_BEFORE" : "_AFTER"), [n, locale.resource("TIME_days")]);
			} else if (dateUtility.getDateDifferenceInYear(new Date(), dt) === 0) {
				return this.getDateInYearString(dt);
			}

			return this.getFormattedDateString(dt, FORMAT_DIFF_YEAR);
		}

		/**
		 * Return true if given enumlist (format) is displayed as an icon
		 * @param {string} format
		 * @return {boolean}
		 */
		function isIconEnum(format) {
			return !!ICON_ENUM_FORMATS[format];
		}

		/**
		 * Return true if given format is one of localized string formats meant for use with a stringmap attribute
		 * @param {string} format
		 * @return {boolean}
		 */
		function isLocalizedStringFormat(format) {
			return format?.indexOf(FORMAT_LOCALIZED) === 0;
		}

		/**
		 * Return true if given attribute type and format support date range filtering
		 * @param {string} type
		 * @param {string} [format]
		 * @return {boolean}
		 */
		function supportsDateRange(type, format) {
			return (type == xml.ATTR_TIME) && (format != FORMAT_TIME);
		}

		return {
			DateRangeSelection,

			// column width constants
			XSMALL,
			SMALL,
			MEDIUM,
			LARGE,
			XLARGE,
			COLUMN_WIDTHS,
			COLUMN_WIDTH_CHECKBOX,
			COLUMN_WIDTH_MAX,
			COLUMN_WIDTH_MIN,
			COLUMN_WIDTH_RE,

			// formats
			FORMAT_ALPHANUMERIC,
			FORMAT_BARCODE,
			FORMAT_COMMENT,
			FORMAT_COMMENT_LATEST,
			FORMAT_DATE_SHORT,
			FORMAT_EMAIL,
			FORMAT_PHONE,
			FORMAT_INTEGER,
			FORMAT_REFERENCED_IMAGE,
			FORMAT_RICH,
			FORMAT_SELECTION,
			FORMAT_TAG: "Tag",
			FORMAT_TEXTAREA,
			FORMAT_TIME_SHORT,
			FORMAT_TYPE: "Type",
			FORMAT_URL,
			FORMAT_IN_FRACTION: "IN - Fraction",		// display fractions by power of 2 up to 16

			// stringmap formats
			FORMAT_LOCALIZED,
			FORMAT_LOCALIZED_RICH,
			FORMAT_LOCALIZED_TEXTAREA,

			// url formats
			FORMAT_IMAGE,

			// time formats
			FORMAT_DATE,
			FORMAT_DATETIME,
			FORMAT_DATETIME_SECONDS,
			FORMAT_TIME,
			FORMAT_DIFF,

			MAX_3DFILE_SIZE,
			UNIVERSAL_DATE: FORMAT_UNIVERSAL_DATE,
			UNIVERSAL_TIME: FORMAT_UNIVERSAL_TIME,

			formatsByType: {
				"double": [
					"",
					"0",
					"0.#",
					"0.##",
					"0.###",
					"0.####",
					"0.0",
					"0.00",
					"0.000",
					"0.0000",
					"#,##0",
					"#,##0.#",
					"#,##0.##",
					"#,##0.###",
					"#,##0.####",
					"#,##0.0",
					"#,##0.00",
					"#,##0.000",
					"#,##0.0000",
					"$#,##0",
					"$#,##0.0",
					"$#,##0.00",
					"$#,##0.000",
					"$#,##0.0000",
					"0%",
					"0.0%",
					"0.00%",
					"0.000%",
					"0.0000%",
					"0.#%",
					"0.##%",
					"0.###%",
					"0.####%"
				],
				"integer": [
					"",
					"0",
					"#,##0",
					"$#,##0"
				],
				"string": [
					"",
					"Alphanumeric",
					"Barcode",
					"Comment",
					"CommentLatest",
					"Email",
					"Phone",
					"Rich",
					"Textarea",
					"URL"
				],
				"time": [
					"Date",
					"Time",
					"DateTime",
					"DateTimeSeconds",
					"DateTimeZone",
					"Diff",
					"UniversalDate",
					"UniversalTime"
				],
				"stringmap": [
					"",
					"Alphanumeric",
					"Barcode",
					"Email",
					"Phone",
					"Rich",
					"Textarea",
					"URL",
					"Localized",
					"LocalizedTextarea",
					"LocalizedRich"
				],
			},

			columnWidthByType: {
				boolean: XSMALL,

				// shown in debug views and usually have longer version of URLs (more fixed) rather than numbered
				cnl: LARGE,
			},

			columnWidthByFormat: {
				// large string formats
				Comment: LARGE,
				CommentLatest: LARGE,
				LocalizedRich: LARGE,
				LocalizedTextarea: LARGE,
				Rich: LARGE,
				Textarea: LARGE,

				// number format
				"0": XSMALL,
				"0.#": XSMALL,
				"0.##": XSMALL,
				"0.###": XSMALL,
				"0.0": XSMALL,
				"0.00": XSMALL,
				"0.000": XSMALL,
				"#,##0": XSMALL,
				"0%": XSMALL,
				"0.0%": XSMALL,
				"0.00%": XSMALL,
				"0.#%": XSMALL,
				"0.##%": XSMALL,

				// time
				DateTime: MEDIUM,
				DateTimeSeconds: MEDIUM,
				Diff: MEDIUM,
			},

			columnWidthBySpecificAttribute: {
				Sizes: MEDIUM,
				Description: LARGE
			},

			addFormat: function(/*String*/type, /*String*/fmt) {
				// summary: add to list of available number formats - double and integer are only ones that can be customized
				this.formatsByType[type].push(fmt);
			},

			/**
			 * Get the column width by format and type
			 * @param {string} [type]
			 * @param {string} [format]
			 * @return {string}
			*/
			getColumnWidthByFormatAndType: function(type, format) {
				if (isIconEnum(format)) {
					return XSMALL;
				}

				return this.columnWidthByFormat[format] || this.columnWidthByType[type] || "";
			},

			/**
			 * Get the column width by specific attribute ID
			 * @param {string} id - the attribute id
			 * @return {string}
			*/
			getColumnWidthByAttributeId: function(id) {
				return this.columnWidthBySpecificAttribute[id] || "";
			},

			/**
			 * Get the column width by given id, type and format,
			 * Note: under the case of Form
			 * @param {string} id - the attribute id
			 * @param {string} type - the attribute type
			 * @param {string} format - the attribute format
			 * @return {string}
			*/
			getAttributeDefaultColumnWidth: function(id, type, format) {
				const baseType = xml.attributeTypeGetBase(type) || type;
				const isContainer = (type != baseType);

				const defaultWidthValue = this.getColumnWidthByAttributeId(id) || (isContainer ? LARGE : (this.getColumnWidthByFormatAndType(type, format) || SMALL));
				return xml.enumCreate("ColumnWidthType", defaultWidthValue);
			},

			getDefaultNumberFormat: function(type) {
				if (xml.attributeTypeIsInteger(type)) {
					return numberUtility.DEFAULT_INTEGER_FORMAT;
				} else if (xml.attributeTypeIsNumeric(type)) {
					return numberUtility.DEFAULT_DECIMAL_FORMAT;
				}
				return null;
			},

			getDefaultNumberOfDecimals: function() {
				return numberUtility.DEFAULT_DECIMALS;
			},

			getFormats: function(/*String*/type) {
				// summary: return formats available for given type - float supports same formats as double
				type = type.replace(/^float/, xml.ATTR_DOUBLE);
				return this.formatsByType[type] || this.formatsByType[xml.attributeTypeGetBase(type)];
			},

			/**
			 * Return localized number format
			 * @param {string} format
			 * @return {string}
			 */
			getLocalizedFormat: function(format) {
				if (format.indexOf("0") > -1 || format.indexOf("#") > -1) {
					return numberUtility.numberGetLocalizedFormat(format);
				}
				return locale.getFormat(format);
			},

			getStringTags: function(/*String*/s) {
				// summary: split string by words or quoted strings
				// Reference: http://www.sitepoint.com/forums/showthread.php?t=441675
				if (!s) {
					return [];
				}

				const a = s.trim().match(RE_TAGS);
				if (!a) {
					return [];
				}

				// remove surrounding quotes on string token if found
				return a.map((s) => {
					return (s.charAt(0) == '"') ? s.substring(1, s.length - 1) : s;
				});
			},

			parseSerializedHyperlink: function(/*String*/value) {
				// summary: given the serialized value of string in "URL" format, returns the object in the format:
				// { name: <name-of-hyperlink>, addr: <address-of-hyperlink> }
				var result;

				try {
					result = JSON.parse(value);
				} catch (e) {
					// support the old format
					result = {
						name: xml.enumGetId(value),
						addr: xml.enumGetText(value)
					};
				}

				return result;
			},

			serializeHyperlink: function(/*String*/name, /*String*/addr) {
				// summary: serialize the string in the format of "URL"
				name = name || "";
				addr = addr || "";
				var obj = {name: name, addr: addr};
				return JSON.stringify(obj);
			},

			addDaysToDate: dateUtility.addDaysToDate,

			/**
			 * Return string representation of given value, where value is either single value (in milliseconds) or a range value which is an
			 * array of 3 numbers, the first two being time values (in milliseconds) and the third being a DateRangeSelection
			 * @param {number|number[]} value
			 * @param {boolean} bFloatingDates - indicates that values are stored relative to 0 rather than absolutely
			 * @return {string}
			 */
			formatDateRange: function(value, bFloatingDates) {
				var self = this;
				var s;

				function _formatDate(dt, fmt) {
					return self.formatTime(self.getDateTime(dt), fmt);
				}

				if (value && _.isNumber(value)) {
					return bFloatingDates ? this.getTimeDiffString(value) : _formatDate(new Date(value));
				}

				if (!value || !value.length) {
					return "";
				}

				// if date range selection involves a relative range, then just return localized name for range
				var selection = value[2];
				var idRes = DateRangeResources[selection];
				if (idRes) {
					s = locale.resource(idRes);
				}

				var startTime = value[0];
				var endTime = value[1];
				var dt1 = new Date(startTime);
				var dt2 = new Date(endTime);
				var start = _formatDate(dt1);
				var end = _formatDate(dt2);

				if (start == end) {
					var MS_IN_DAY = 1000 * 60 * 60 * 24 - 60000;
					var timeDiff = endTime - startTime;

					if (!timeDiff || (timeDiff >= MS_IN_DAY)) {
						// if filter is for full day, then just show date
						s = s || start;
					} else {
						// show single date plus time range
						if (bFloatingDates) {
							start = this.getTimeDiffString(startTime);
						}

						s = locale.resource("DateRangeDayAndTime", [s || start, _formatDate(dt1, FORMAT_TIME), _formatDate(dt2, FORMAT_TIME)]);
					}
				} else if (bFloatingDates) {
					// show floating date range
					s = s || locale.resource("DateRangeFloating", [this.getTimeDiffString(startTime), this.getTimeDiffString(endTime)]);
				} else {
					// show date range
					s = s || locale.resource("DateRange", [start, end]);
				}

				return s;
			},

			formatTime: dateUtility.formatTime,

			getAbsoluteDateString: dateUtility.getAbsoluteDateString,

			getDaysToDate: dateUtility.getDaysToDate,

			getDateMonthString: dateUtility.getDateMonthString,

			getDatePattern: dateUtility.getDatePattern,

			getDateRangeString: function(/*Date*/dt1, /*Date*/dt2, /*Boolean*/bInclusive) {
				// summary: given the date at the start of the week, return string in form of Jan 1 - 7, or Jan 28 - Feb 4 if
				// start and end months are different
				var f1, f2;

				f1 = f2 = FORMAT_SAME_YEAR;

				if (!bInclusive) {
					dt2 = this.addDaysToDate(dt2, -1, true);
				}

				if (dateUtility.getDateDifferenceInMonth(dt1, dt2) == 0) {
					var df = dateUtility.getFormatPattern(FORMAT_DATE_SHORT);
					if (df.indexOf("d") < df.indexOf("M")) {
						f1 = FORMAT_SAME_DAY;
					} else {
						f2 = FORMAT_SAME_DAY;
					}
				}

				return locale.resource("DateRange", [
					this.getFormattedDateString(dt1, f1),
					this.getFormattedDateString(dt2, f2)
				]);
			},

			/**
			 * Returns time value in milliseconds of given date, optionally indicating if time is wanted at the start of the date (midnight)
			 * @param {Date} [dt] - if no date given, then uses current date/time
			 * @param {boolean} [startOfDay]
			 * @return {number}
			 */
			getDateTime: dateUtility.getDateTime,

			/**
			 * Return default format for specified locale and type
			 * @param {string} localeName - short name of locale, like "en", "fr" etc
			 * @param {string} formatName - name of desired format, like "dateFormat-short", "timeFormat-short"
			 * @return {string}
			 */
			getDefaultDateTimeFormat: dateUtility.getDefaultDateTimeFormat,

			/**
			 * Returns epoch time for current time-zone without any conversions. Usually, when getting epoch time,
			 * it will convert the date to UTC first then gets the milliseconds from 1970 Jan 1st. We're not doing the conversion here.
			 * @param {Date} [dt] - optional Date to convert into epoch, if none given then a new Date now will be created.
			 * @param {number} [hours] - optional parameter to add hours onto the UTCDateTime.
			 * @return {number} milliseconds from 1st Jan 1970 until the date passed in (includes the time-zone, so no conversion to UTC is done on the date)
			 */
			getUTCDateTime: dateUtility.getUTCDateTime,

			getDurationString: function(/*Number*/start, /*Number*/end) {
				// compares dates and convert to hours: minutes or days hours: minutes format
				// accepts timestamp
				var totalMin = end ? dateUtility.getDateDifferenceInMinutes(new Date(start), new Date(end)) : new Date(start);
				totalMin = Math.abs(totalMin);
				var min = (totalMin % 60 / 100).toFixed(2).split(".")[1];
				totalMin /= 60;
				var hrs = Math.floor(totalMin % 24);
				totalMin /= 24;
				var days = Math.floor(totalMin);

				if (!days) {
					return locale.resource("TIME_hrs", [hrs, min]);
				}

				if (hrs === 0 && min === '00') {
					return locale.resource(days > 1 ? "TIME_calDays" : "TIME_calDay", [days]);
				}

				return locale.resource(days > 1 ? "TIME_daysHrs" : "TIME_dayHrs", [days, hrs, min]);
			},

			getTimeFormatOptions: dateUtility.getTimeFormatOptions,

			getTimeRange: dateUtility.getTimeRange,

			getTimeDiffString: function(/*Number*/ms) {
				// summary: convert milliseconds to string indicating appropriate range (i.e. 7 days)
				var before = (ms < 0);
				var minute = 60000;
				var hour = 3600000;
				var day = 86400000;
				var x;

				if (before) {
					ms = -ms;
				}

				if (ms < minute) {
					x = _formatTime(ms, 1000, "second", before);
				} else if (ms < hour) {
					x = _formatTime(ms, minute, "minute", before);
				} else if (ms < day) {
					x = _formatTime(ms, hour, "hour", before);
				} else {
					var days = Math.round(ms / day);
					var weeks = Math.round(days / 7);

					if (days == weeks * 7) {
						x = _formatTime(ms, 7 * day, "week", before);
					} else {
						x = _formatTime(ms, day, "day", before);
					}
				}

				return x;
			},

			getWeekStartDate: dateUtility.getWeekStartDate,

			isArabicString: function(/*String*/ str) { //DE29059: [TAO] Translation in Arabic is not correct in the Technical File
				var arabic = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF]/;
				return arabic.test(str);
			},

			isLocalizedStringFormat,

			isTimeFormat: dateUtility.isTimeFormat,

			isTimeToday: dateUtility.isTimeToday,

			showFullYear: dateUtility.showFullYear,

			// util functions
			abbreviateNumber: numberUtility.abbreviateNumber,
			getNumberDecimal: numberUtility.getNumberDecimal,
			getNumberGroup: numberUtility.getNumberGroup,
			getNumberRead: numberUtility.formatNumber,
			getNumberWrite: numberUtility.formatNumberForEditing,
			getInternalNumber: numberUtility.getInternalNumber,
			getFractionString: numberUtility.getFractionString,
			getFraction: numberUtility.getFraction,
			getFractionValue: numberUtility.getFractionValue,
			getIntermediateFraction: numberUtility.getIntermediateFraction,
			getIntermediateSortValue: numberUtility.numberGetIntermediateSortValue,
			greatestCommonDenominator: numberUtility.greatestCommonDenominator,
			roundToPrecision: _round,

			// number functions
			getNumberRange: numberUtility.getNumberRange,
			getPercentageNumber: numberUtility.getPercentageNumber,

			// date functions
			combineDateAndTime: dateUtility.combineDateAndTime,
			convertDateRangeToUTC: dateUtility.convertDateRangeToUTC,
			getDateDifference: dateUtility.getDateDifference,
			getDateInYearString: dateUtility.getDateInYearString,
			getDateRangeRes: dateUtility.getDateRangeRes,
			getFormattedDateDifference,
			getFormattedDateString: dateUtility.getFormattedDateString,
			getDateRange: dateUtility.getDateRange,
			getEndOfDay: dateUtility.getEndOfDay,
			getStartOfDay: dateUtility.getStartOfDay,
			getTimeDate: dateUtility.getTimeDate,
			getUTCTimeDate: dateUtility.getUTCTimeDate,
			hoursToMS: dateUtility.hoursToMS,
			isDateRangeBlanks: dateUtility.isDateRangeBlanks,
			isDateRangeNonBlanks: dateUtility.isDateRangeNonBlanks,
			isDateRangeSelectionRelative: dateUtility.isDateRangeSelectionRelative,
			isDateRangeValue: dateUtility.isDateRangeValue,
			supportsDateRange,
			updateDateRange: dateUtility.updateDateRange,

			// enum functions
			isIconEnum
		};
	});
